/**
 * BreakoutSessionsManager - Admin UI for managing breakout sessions
 * Create/edit sessions, enroll delegates, auto-assign by age group
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  Users, Plus, Edit2, Trash2, UserPlus, Zap, Clock, MapPin,
  Loader2, CheckCircle, XCircle, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AGE_GROUPS = ['under-10', '10-12', '13-15', '16-18', '19-25', '26-35', '36+'];

const BreakoutSessionsManager = ({ eventId }) => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', age_groups: [], capacity: 0,
    venue: '', start_time: '', end_time: '', facilitator: ''
  });

  const authHeaders = { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' };

  // Fetch sessions
  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['breakout-sessions', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/breakout-sessions`, { headers: authHeaders });
      return res.ok ? res.json() : [];
    }
  });

  // Fetch enrollments for expanded session
  const { data: enrollments = [] } = useQuery({
    queryKey: ['breakout-enrollments', eventId, expandedSession],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/breakout-sessions/${expandedSession}/enrollments`, { headers: authHeaders });
      return res.ok ? res.json() : [];
    },
    enabled: !!expandedSession
  });

  // Fetch age group stats for auto-assign info
  const { data: ageStats = [] } = useQuery({
    queryKey: ['age-group-stats', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/age-groups/stats`, { headers: authHeaders });
      return res.ok ? res.json() : [];
    }
  });

  // CRUD mutations
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const url = editSession
        ? `${API_URL}/api/events/${eventId}/breakout-sessions/${editSession.id}`
        : `${API_URL}/api/events/${eventId}/breakout-sessions`;
      const res = await fetch(url, { method: editSession ? 'PUT' : 'POST', headers: authHeaders, body: JSON.stringify(data) });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      return res.json();
    },
    onSuccess: () => {
      toast.success(editSession ? 'Session updated' : 'Session created');
      qc.invalidateQueries({ queryKey: ['breakout-sessions'] });
      resetForm();
    },
    onError: (e) => toast.error(e.message)
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/breakout-sessions/${id}`, { method: 'DELETE', headers: authHeaders });
      return res.json();
    },
    onSuccess: () => { toast.success('Session deleted'); qc.invalidateQueries({ queryKey: ['breakout-sessions'] }); }
  });

  const autoAssignMutation = useMutation({
    mutationFn: async (sessionId) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/breakout-sessions/${sessionId}/auto-assign`, { method: 'POST', headers: authHeaders });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(`Auto-assigned ${data.assigned} delegates`);
      qc.invalidateQueries({ queryKey: ['breakout-sessions'] });
      qc.invalidateQueries({ queryKey: ['breakout-enrollments'] });
    },
    onError: (e) => {
      toast.error(e.message);
      // A "seats just taken" refusal means our counts are stale — refresh.
      qc.invalidateQueries({ queryKey: ['breakout-sessions'] });
      qc.invalidateQueries({ queryKey: ['breakout-enrollments'] });
    }
  });

  const unenrollMutation = useMutation({
    mutationFn: async ({ sessionId, delegateId }) => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/breakout-sessions/${sessionId}/enroll/${delegateId}`, { method: 'DELETE', headers: authHeaders });
      return res.json();
    },
    onSuccess: () => {
      toast.success('Delegate removed');
      qc.invalidateQueries({ queryKey: ['breakout-sessions'] });
      qc.invalidateQueries({ queryKey: ['breakout-enrollments'] });
    }
  });

  const resetForm = () => {
    setShowForm(false);
    setEditSession(null);
    setFormData({ name: '', description: '', age_groups: [], capacity: 0, venue: '', start_time: '', end_time: '', facilitator: '' });
  };

  const openEdit = (s) => {
    setEditSession(s);
    setFormData({ name: s.name, description: s.description || '', age_groups: s.age_groups || [], capacity: s.capacity || 0, venue: s.venue || '', start_time: s.start_time || '', end_time: s.end_time || '', facilitator: s.facilitator || '' });
    setShowForm(true);
  };

  const toggleAgeGroup = (ag) => {
    setFormData(prev => ({
      ...prev,
      age_groups: prev.age_groups.includes(ag)
        ? prev.age_groups.filter(a => a !== ag)
        : [...prev.age_groups, ag]
    }));
  };

  const totalEnrolled = sessions.reduce((s, sess) => s + (sess.enrolled_count || 0), 0);
  const totalCapacity = sessions.reduce((s, sess) => s + (sess.capacity || 0), 0);

  return (
    <div className="space-y-6" data-testid="breakout-sessions-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Breakout Sessions</h2>
          <p className="text-sm text-slate-500">{sessions.length} sessions, {totalEnrolled} enrolled{totalCapacity > 0 ? ` / ${totalCapacity} capacity` : ''}</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-session-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Session
        </Button>
      </div>

      {/* Age Group Stats */}
      {ageStats.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 mb-2">Delegate Age Distribution</p>
            <div className="flex flex-wrap gap-2">
              {ageStats.map(g => (
                <Badge key={g.age_group} variant="outline" className="text-xs py-1 px-2">
                  {g.age_group}: <span className="font-bold ml-1">{g.count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sessions Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : sessions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-500">No Breakout Sessions Yet</h3>
            <p className="text-sm text-slate-400 mt-1">Create sessions to group delegates by age for workshops and activities</p>
            <Button className="mt-4" onClick={() => setShowForm(true)}><Plus className="h-4 w-4 mr-2" /> Create Session</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sessions.map(s => {
            const pct = s.capacity > 0 ? Math.round((s.enrolled_count / s.capacity) * 100) : 0;
            const isExpanded = expandedSession === s.id;
            return (
              <Card key={s.id} className={isExpanded ? 'ring-2 ring-indigo-400' : ''} data-testid={`session-card-${s.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">{s.name}</h3>
                        {s.age_groups?.length > 0 && s.age_groups.map(ag => (
                          <Badge key={ag} variant="secondary" className="text-xs">{ag}</Badge>
                        ))}
                      </div>
                      {s.description && <p className="text-sm text-slate-500 mb-2">{s.description}</p>}
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        {s.venue && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{s.venue}</span>}
                        {s.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.start_time}{s.end_time ? ` - ${s.end_time}` : ''}</span>}
                        {s.facilitator && <span>Facilitator: {s.facilitator}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-4">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => autoAssignMutation.mutate(s.id)} disabled={autoAssignMutation.isPending || !s.age_groups?.length} data-testid={`auto-assign-${s.id}`}>
                        <Zap className="h-3 w-3 mr-1" /> Auto
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => openEdit(s)} data-testid={`edit-session-${s.id}`}>
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-red-500 hover:text-red-700" onClick={() => { if (confirm('Delete this session?')) deleteMutation.mutate(s.id); }} data-testid={`delete-session-${s.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Capacity bar */}
                  <div className="mt-3">
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="text-slate-500">{s.enrolled_count} enrolled</span>
                      <span className="text-slate-500">{s.capacity > 0 ? `${s.spots_remaining} spots left` : 'Unlimited'}</span>
                    </div>
                    {s.capacity > 0 && <Progress value={pct} className="h-2" />}
                  </div>

                  {/* Expand/collapse enrolled delegates */}
                  <button
                    className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                    onClick={() => setExpandedSession(isExpanded ? null : s.id)}
                    data-testid={`expand-enrollments-${s.id}`}
                  >
                    {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {isExpanded ? 'Hide' : 'Show'} enrolled delegates ({s.enrolled_count})
                  </button>

                  {isExpanded && (
                    <div className="mt-3 border-t pt-3">
                      {enrollments.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-3">No delegates enrolled yet</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow><TableHead>Name</TableHead><TableHead>Age Group</TableHead><TableHead>Enrolled At</TableHead><TableHead></TableHead></TableRow>
                          </TableHeader>
                          <TableBody>
                            {enrollments.map((e, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-medium text-sm">{e.delegate_name}</TableCell>
                                <TableCell><Badge variant="outline" className="text-xs">{e.age_group || '-'}</Badge></TableCell>
                                <TableCell className="text-xs text-slate-400">{e.enrolled_at ? new Date(e.enrolled_at).toLocaleString() : '-'}</TableCell>
                                <TableCell>
                                  <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500" onClick={() => unenrollMutation.mutate({ sessionId: s.id, delegateId: e.delegate_id })}>
                                    <XCircle className="h-3 w-3 mr-1" /> Remove
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editSession ? 'Edit Session' : 'New Breakout Session'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Session Name *</Label>
              <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Youth Workshop" data-testid="session-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="What this session covers..." rows={2} />
            </div>
            <div>
              <Label className="mb-2 block">Age Groups (click to select)</Label>
              <div className="flex flex-wrap gap-2">
                {AGE_GROUPS.map(ag => (
                  <button key={ag} type="button" onClick={() => toggleAgeGroup(ag)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      formData.age_groups.includes(ag)
                        ? 'bg-indigo-100 border-indigo-400 text-indigo-700 font-medium'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid={`age-group-${ag}`}
                  >
                    {ag}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Leave empty for all ages</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Capacity (0 = unlimited)</Label>
                <Input type="number" min="0" value={formData.capacity} onChange={e => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })} data-testid="session-capacity-input" />
              </div>
              <div>
                <Label>Venue</Label>
                <Input value={formData.venue} onChange={e => setFormData({ ...formData, venue: e.target.value })} placeholder="Hall B" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={formData.start_time} onChange={e => setFormData({ ...formData, start_time: e.target.value })} />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={formData.end_time} onChange={e => setFormData({ ...formData, end_time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Facilitator</Label>
              <Input value={formData.facilitator} onChange={e => setFormData({ ...formData, facilitator: e.target.value })} placeholder="Pastor James" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={() => createMutation.mutate(formData)} disabled={!formData.name || createMutation.isPending}>
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editSession ? 'Update' : 'Create'} Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BreakoutSessionsManager;
